import { getAuthToken } from './auth';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '/api';

function authHeaders() {
  const token = getAuthToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

function buildQuery(params = {}) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') q.append(k, String(v));
  });
  const s = q.toString();
  return s ? `?${s}` : '';
}

async function handleResponse(res) {
  let data = null;
  try {
    data = await res.json();
  } catch (_) {
    // ignore json parse error
  }
  if (!res.ok) {
    const message = (data && (data.message || data.error)) || `Request failed (${res.status})`;
    const err = new Error(message);
    err.status = res.status;
    err.data = data;
    if (res.status === 402 || (data && (data.error === 'UPGRADE_REQUIRED'))) {
      err.code = 'UPGRADE_REQUIRED';
    }
    throw err;
  }
  return data;
}

// Groups
export async function listGroups(params = {}) {
  const url = `${API_BASE}/groups${buildQuery(params)}`;
  const res = await fetch(url, { headers: authHeaders() });
  return handleResponse(res);
}

export async function getGroup(groupId) {
  const res = await fetch(`${API_BASE}/groups/${groupId}`, { headers: authHeaders() });
  return handleResponse(res);
}

export async function createGroup(payload) {
  const res = await fetch(`${API_BASE}/groups`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

export async function updateGroup(groupId, payload) {
  const res = await fetch(`${API_BASE}/groups/${groupId}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

export async function archiveGroup(groupId) {
  const res = await fetch(`${API_BASE}/groups/${groupId}/archive`, { method: 'POST', headers: authHeaders() });
  return handleResponse(res);
}

export async function deleteGroup(groupId) {
  const res = await fetch(`${API_BASE}/groups/${groupId}`, { method: 'DELETE', headers: authHeaders() });
  return handleResponse(res);
}

// Membership
export async function joinGroup(groupId) {
  const res = await fetch(`${API_BASE}/groups/${groupId}/join`, { method: 'POST', headers: authHeaders() });
  return handleResponse(res);
}

export async function requestJoin(groupId) {
  const res = await fetch(`${API_BASE}/groups/${groupId}/request`, { method: 'POST', headers: authHeaders() });
  return handleResponse(res);
}

export async function leaveGroup(groupId) {
  const res = await fetch(`${API_BASE}/groups/${groupId}/leave`, { method: 'POST', headers: authHeaders() });
  return handleResponse(res);
}

export async function inviteMember(groupId, identifier) {
  // identifier: { username?: string, email?: string }
  const res = await fetch(`${API_BASE}/groups/${groupId}/invite`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(identifier),
  });
  return handleResponse(res);
}

export async function acceptInvitation(groupId) {
  const res = await fetch(`${API_BASE}/groups/${groupId}/invitations/accept`, { method: 'POST', headers: authHeaders() });
  return handleResponse(res);
}

export async function declineInvitation(groupId) {
  const res = await fetch(`${API_BASE}/groups/${groupId}/invitations/decline`, { method: 'POST', headers: authHeaders() });
  return handleResponse(res);
}

export async function approveRequest(groupId, userId) {
  const res = await fetch(`${API_BASE}/groups/${groupId}/requests/${userId}/approve`, { method: 'POST', headers: authHeaders() });
  return handleResponse(res);
}

export async function denyRequest(groupId, userId) {
  const res = await fetch(`${API_BASE}/groups/${groupId}/requests/${userId}/deny`, { method: 'POST', headers: authHeaders() });
  return handleResponse(res);
}

export async function assignAdmin(groupId, userId) {
  const res = await fetch(`${API_BASE}/groups/${groupId}/admins/${userId}`, { method: 'POST', headers: authHeaders() });
  return handleResponse(res);
}

export async function removeMember(groupId, userId) {
  const res = await fetch(`${API_BASE}/groups/${groupId}/members/${userId}`, { method: 'DELETE', headers: authHeaders() });
  return handleResponse(res);
}

// Discussion / Collaboration
export async function listThreads(groupId, params = {}) {
  const res = await fetch(`${API_BASE}/groups/${groupId}/threads${buildQuery(params)}`, { headers: authHeaders() });
  return handleResponse(res);
}

export async function createThread(groupId, payload) {
  // payload: { title, content, attachments?: [{type: 'link', url, title?}] }
  const res = await fetch(`${API_BASE}/groups/${groupId}/threads`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

export async function replyToThread(threadId, payload) {
  // payload: { content, attachments?: [] }
  const res = await fetch(`${API_BASE}/threads/${threadId}/replies`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

export async function reactToPost(postId, reaction = 'like') {
  const res = await fetch(`${API_BASE}/posts/${postId}/reactions`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ reaction }),
  });
  return handleResponse(res);
}

export async function deleteReply(threadId, replyId) {
  const res = await fetch(`${API_BASE}/threads/${threadId}/replies/${replyId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  return handleResponse(res);
}

export async function deleteThread(threadId) {
  const res = await fetch(`${API_BASE}/threads/${threadId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  return handleResponse(res);
}

// Shared assignments
export async function listShared(groupId) {
  const res = await fetch(`${API_BASE}/groups/${groupId}/shared`, { headers: authHeaders() });
  return handleResponse(res);
}
export async function addShared(groupId, payload) {
  // payload: { type: 'task'|'goal', itemId, title?, assignedTo?: string[], dueDate?: string }
  const res = await fetch(`${API_BASE}/groups/${groupId}/shared`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(payload) });
  return handleResponse(res);
}

export async function createGroupTasks(groupId, payload) {
  // payload: { title: string, description?: string, dueDate?: string, assignedTo: string[] }
  const res = await fetch(`${API_BASE}/groups/${groupId}/tasks`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(payload) });
  return handleResponse(res);
}

export async function getGroupTasks(groupId) {
  const res = await fetch(`${API_BASE}/groups/${groupId}/tasks`, { headers: authHeaders() });
  return handleResponse(res);
}

export async function deleteGroupTask(taskId) {
  const res = await fetch(`${API_BASE}/tasks/${taskId}`, { method: 'DELETE', headers: authHeaders() });
  return handleResponse(res);
}

// Task helpers (used by group recent results UI)
export async function updateTaskStatus(taskId, status) {
  const res = await fetch(`${API_BASE}/tasks/${taskId}/status`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify({ status })
  });
  return handleResponse(res);
}

export async function updateTaskCompletion(taskId, percentage) {
  const res = await fetch(`${API_BASE}/tasks/${taskId}/completion`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify({ percentage })
  });
  return handleResponse(res);
}

export async function updateTaskResult(taskId, result) {
  const res = await fetch(`${API_BASE}/tasks/${taskId}/result`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify({ result })
  });
  return handleResponse(res);
}

export async function deleteShared(groupId, sharedId) {
  const res = await fetch(`${API_BASE}/groups/${groupId}/shared/${sharedId}`, { method: 'DELETE', headers: authHeaders() });
  return handleResponse(res);
}

// Resources
export async function listResources(groupId) {
  const res = await fetch(`${API_BASE}/groups/${groupId}/resources`, { headers: authHeaders() });
  return handleResponse(res);
}
export async function addResourceLink(groupId, payload) {
  // payload: { title?, url }
  const res = await fetch(`${API_BASE}/groups/${groupId}/resources/link`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(payload) });
  return handleResponse(res);
}
export async function uploadResourceFile(groupId, file, title) {
  const token = getAuthToken();
  const form = new FormData();
  form.append('file', file);
  if (title) form.append('title', title);
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const res = await fetch(`${API_BASE}/groups/${groupId}/resources/file`, { method: 'POST', headers, body: form });
  return handleResponse(res);
}

// Discovery helpers
export async function relatedGroups(relatedType, relatedId) {
  const res = await fetch(`${API_BASE}/groups${buildQuery({ relatedType, relatedId })}`, { headers: authHeaders() });
  return handleResponse(res);
}

export const searchGroups = listGroups;

// Users
export async function getUsersBasic(ids = []) {
  const API_BASE = process.env.REACT_APP_API_BASE_URL || '/api';
  const q = new URLSearchParams();
  // Support batch via repeated ids parameters for safety (and comma as fallback)
  if (Array.isArray(ids)) {
    ids.filter(Boolean).forEach(id => q.append('ids', String(id)));
  } else if (ids) {
    q.append('ids', String(ids));
  }
  const url = `${API_BASE}/users/basic${q.toString() ? `?${q.toString()}` : ''}`;
  const res = await fetch(url, { headers: authHeaders() });
  return handleResponse(res);
}
